import * as React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  Editor,
  EditorContent,
  EditorContext,
  ReactNodeViewRenderer,
  useEditor,
} from '@tiptap/react';
import { NodeSelection } from '@tiptap/pm/state';

// --- Tiptap Core Extensions ---
import { StarterKit } from '@tiptap/starter-kit';
import { TaskList, TaskItem } from '@tiptap/extension-list';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { Placeholder } from '@tiptap/extensions';
import { Highlight } from '@tiptap/extension-highlight';
import { Superscript } from '@tiptap/extension-superscript';
import { Subscript } from '@tiptap/extension-subscript';
import { TextAlign } from '@tiptap/extension-text-align';
import { UniqueID } from '@tiptap/extension-unique-id';
import {
  Table,
  TableCell,
  TableHeader,
  TableRow,
} from '@tiptap/extension-table';
import { Markdown } from '@tiptap/markdown';

// --- Custom Extensions ---
import { HorizontalRule } from '../../../../ui/tiptap/tiptap-node/horizontal-rule-node/horizontal-rule-node-extension';
import { Section } from '../../../../ui/tiptap/tiptap-node/section-node/section-node-extension';
import { UiState } from '../../../../ui/tiptap/tiptap-extension/ui-state-extension';
import {
  Indent,
  IndentBackspace,
} from '../../../../ui/tiptap/tiptap-extension/indent-extension';
import { CoteraEmoji } from '../../../../ui/tiptap/extensions/emoji';
import {
  CommentMark,
  CommentActiveHighlight,
  CommentDraftHighlight,
  CommentAuthorProvider,
  type CommentAuthor,
} from '../../../../ui/tiptap/extensions/comment-mark';
import {
  CommentInputPopover,
  CommentMarginChips,
  CommentThreadPopover,
} from '../comments';
import {
  SingleH1Extension,
  CustomTypography,
  TitleExtension,
  DocumentTitle,
} from '../../../../ui/tiptap/extensions/single-h1';
import { Image } from '../../../../ui/tiptap/tiptap-node/image-node/image-node-extension';
import { TableGridNodeView } from '../../../../ui/tiptap/extensions/table-grid/table-grid-node-view';
import { insertMarkdownFromPaste } from '../../lib/markdown-paste';
import { useEditableSync } from '../../lib/use-editable-sync';

// --- Tiptap UI ---
import { SlashDropdownMenu } from '../../../../ui/tiptap/tiptap-ui/slash-dropdown-menu';
import { DragContextMenu } from '../../../../ui/tiptap/tiptap-ui/drag-context-menu';
import { FloatingToolbar } from './editor-toolbar-floating';

// --- Contexts ---
import { AppProvider } from '../../../../ui/tiptap/contexts/app-context';
import type { EditorExtensionConfig } from '../../types/extension-config';
import { DG } from '@cotera/sdk/core';
import { cn } from '@cotera/client/v0/lib/utils';

export interface StandaloneEditorProps {
  placeholder?: string | ((props: { editor: Editor; node: any }) => string);
  titlePlaceholder?: string;
  content?: DG.DocumentEditor.DocumentState;
  editable?: boolean;
  /**
   * Place the cursor at the start of the document as soon as the editor mounts,
   * so the user can type immediately (e.g. a freshly created agent). By default
   * the editor primes the caret and then blurs, so nothing is focused until the
   * user clicks.
   */
  autoFocus?: boolean;
  onUpdate?: (editor: Editor) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  extensions?: EditorExtensionConfig;
  /**
   * Optional mention dropdown menu component to render.
   * If not provided, no mention dropdown will be shown.
   * This allows you to pass in custom mention functionality or use the default.
   */
  children?: React.ReactNode;
  className?: string;
  /**
   * Enables inline human-readable comments (Notion-style annotations) authored by this
   * user. When omitted, comment marks are still preserved in saved content but the
   * comment UI (toolbar button + popovers) is hidden.
   */
  commentAuthor?: CommentAuthor;
  /**
   * Called with the editor instance once it exists, and with `null` when it goes away.
   *
   * `ref` already exposes the same instance, but a ref cannot re-render the page that
   * holds it, and surfaces mounted outside this tree — the comments rail beside the
   * document, the comment count in the page header — have to subscribe to the editor
   * to stay current.
   */
  onEditorReady?: (editor: Editor | null) => void;
}

export interface RichTextEditorRef {
  editor: Editor | null;
}

/**
 * Loading spinner component
 */
export function LoadingSpinner({ text = 'Loading...' }: { text?: string }) {
  return (
    <div className="spinner-container">
      <div className="spinner-content">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10"></circle>
          <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <div className="spinner-loading-text">{text}</div>
      </div>
    </div>
  );
}

/**
 * EditorContent component that renders the actual editor
 */
export function EditorContentArea({
  children,
}: {
  children?: React.ReactNode;
}) {
  const { editor } = React.useContext(EditorContext)!;

  if (!editor) {
    return null;
  }

  return (
    <EditorContent
      editor={editor}
      role="presentation"
      className="notion-like-editor-content"
    >
      <DragContextMenu />
      {children}
      <SlashDropdownMenu />
      <FloatingToolbar />
      <CommentInputPopover />
      <CommentThreadPopover />
      {/* In the flow of the editor rather than floating over it: the chips are
          measured against this box and scroll with the prose they annotate, which a
          fixed floating element would have to chase on every scroll event. */}
      <CommentMarginChips />
    </EditorContent>
  );
}

/**
 * Standalone editor without collaboration or AI features
 */
export const StandaloneEditor = React.forwardRef<
  RichTextEditorRef,
  StandaloneEditorProps
>(function StandaloneEditor(
  {
    placeholder = 'Start writing...',
    titlePlaceholder,
    content = { type: 'doc', content: [] },
    editable = true,
    autoFocus = false,
    onUpdate,
    onFocus,
    onBlur,
    extensions = [],
    children,
    className,
    commentAuthor,
    onEditorReady,
  },
  ref
) {
  // Build extensions array from configuration
  const buildExtensions = () => {
    const baseExtensions = [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4],
        },
        horizontalRule: false,
      }),
      UiState,
      Indent,
      IndentBackspace,
      HorizontalRule,
      Section,
      Image.configure({
        allowBase64: true,
      }),
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      // Rendered by the data-grid node view. `resizable` is off because the
      // column-resizing plugin manages the default table DOM, which the custom
      // node view replaces; edits flow back through the grid's handlers.
      Table.extend({
        addNodeView() {
          return ReactNodeViewRenderer(TableGridNodeView);
        },
      }).configure({
        resizable: false,
        allowTableNodeSelection: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      Color,
      TextStyle,
      Highlight.configure({
        multicolor: true,
      }),
      Placeholder.configure({
        placeholder,
      }),
      DocumentTitle.configure({
        placeholder: titlePlaceholder || 'Untitled',
      }),
      SingleH1Extension,
      CustomTypography,
      TitleExtension,
      Superscript,
      Subscript,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      UniqueID.configure({
        types: ['heading', 'paragraph', 'codeBlock', 'section', 'disclaimer'],
      }),
      Markdown,
      CoteraEmoji,
      // Registered unconditionally so saved comment annotations are never dropped from
      // the schema on load. The comment *UI* is enabled per-instance via `commentAuthor`.
      CommentMark,
      // Paints the thread the caret is in, which is how picking a card out of the
      // comments rail shows up in the prose. Decorations only — nothing it does
      // reaches the saved document.
      CommentActiveHighlight,
      // Keeps the selected words visible while a new comment is being written, once
      // the composer has taken focus away from the editor.
      CommentDraftHighlight,
    ];

    // Apply custom extension configurations
    const configuredExtensions = extensions.map(({ extension, config }) => {
      if (config) {
        return extension.configure(config);
      }
      return extension;
    });

    return [...baseExtensions, ...configuredExtensions];
  };

  const editor = useEditor({
    extensions: buildExtensions(),
    content,
    editable,
    autofocus: false,
    // Report (don't swallow) documents whose stored `editor_state` does not
    // satisfy the current schema. `emitContentError` keeps the existing lenient
    // parse — it does not change what loads — but surfaces the offending node so
    // a corrupt document can be found and repaired instead of only crashing. The
    // error message includes the bad node (e.g. the document's title heading),
    // which is enough to locate the row.
    emitContentError: true,
    onContentError: ({ error }) => {
      // eslint-disable-next-line no-console
      console.error('[rte] invalid editor content', error);
    },
    editorProps: {
      attributes: {
        class: 'tiptap ProseMirror notion-like-editor',
      },
      handleDOMEvents: {
        focus: () => {
          if (onFocus) {
            onFocus();
          }
          return false;
        },
        blur: () => {
          if (onBlur) {
            onBlur();
          }
          return false;
        },
      },
      handleKeyDown: (view, event): boolean => {
        const { selection } = view.state;
        const isDeleteKey = event.key === 'Backspace' || event.key === 'Delete';
        if (
          isDeleteKey &&
          selection instanceof NodeSelection &&
          selection.node.type.name === 'table'
        ) {
          event.preventDefault();
          return editor?.chain().focus().deleteSelection().run() ?? false;
        }

        return false;
      },
      handlePaste: (_view, event) => {
        if (editor !== null && insertMarkdownFromPaste(editor, event)) {
          return true;
        }

        const clipboardData = event.clipboardData;
        if (!clipboardData) {
          return false;
        }

        const imageFiles = Array.from(clipboardData.files).filter((file) =>
          file.type.startsWith('image/')
        );

        if (imageFiles.length === 0) {
          return false;
        }

        event.preventDefault();

        imageFiles.forEach((file) => {
          if (editor === null || editor === undefined) {
            return;
          }

          const reader = new FileReader();
          reader.onload = () => {
            if (
              typeof reader.result === 'string' &&
              editor.isEditable &&
              editor
                .chain()
                .focus()
                .setImage({
                  src: reader.result,
                  alt: file.name,
                  title: file.name,
                })
                .run()
            ) {
              return;
            }
          };
          reader.readAsDataURL(file);
        });

        return true;
      },
    },
    onCreate: ({ editor }) => {
      // Set cursor to start position to prevent select-all on first click
      editor.commands.focus('start', { scrollIntoView: false });
      // Keep focus for callers that want the user typing right away (e.g. a new
      // agent); otherwise blur so nothing is focused until the user clicks.
      if (!autoFocus) {
        editor.commands.blur();
      }
    },
    onUpdate: ({ editor }) => {
      if (onUpdate) {
        onUpdate(editor);
      }
    },
  });

  // Expose editor instance via ref
  React.useImperativeHandle(
    ref,
    () => ({
      editor,
    }),
    [editor]
  );

  React.useEffect(() => {
    if (onEditorReady === undefined) {
      return;
    }
    onEditorReady(editor);
    return () => onEditorReady(null);
  }, [editor, onEditorReady]);

  // Tiptap v3's `useEditor` only applies `editable` at creation, so a document
  // artifact mounted read-only while Coco generates would stay stuck read-only
  // after it unlocks. Push prop changes onto the live instance.
  useEditableSync(editor, editable);

  if (editor == null) {
    return <LoadingSpinner />;
  }

  return (
    <div className={cn('notion-like-editor-wrapper', className)}>
      <EditorContext.Provider value={{ editor }}>
        <CommentAuthorProvider value={commentAuthor ?? null}>
          <EditorContentArea>{children}</EditorContentArea>
        </CommentAuthorProvider>
      </EditorContext.Provider>
    </div>
  );
});

/**
 * Shown in place of the editor when a document cannot be rendered. In practice this
 * means the stored `editor_state` holds a node the current schema rejects, which makes
 * ProseMirror throw while normalizing the document (e.g. during id assignment on the
 * first transaction). Containing that here keeps a single corrupt document from taking
 * down the whole app: the reader sees a clear message, and the rest of Cotera — nav,
 * other documents, everything outside this editor — stays usable.
 */
function EditorErrorFallback(): React.ReactElement {
  return (
    <div className="notion-like-editor-wrapper">
      <div className="h-full flex flex-col items-center justify-center text-center p-8">
        <p className="text-primary-text font-medium mb-2">
          This document couldn’t be displayed
        </p>
        <p className="text-primary-text text-sm opacity-70 max-w-md">
          Something in this document is preventing it from opening. The rest of
          Cotera is unaffected. Please{' '}
          <a className="underline" href="mailto:support@cotera.co">
            contact support
          </a>{' '}
          and we’ll repair it.
        </p>
      </div>
    </div>
  );
}

/**
 * Full standalone editor with app provider
 */
export const RichTextEditor = React.forwardRef<
  RichTextEditorRef,
  StandaloneEditorProps
>((props, ref) => {
  return (
    <AppProvider>
      <ErrorBoundary fallbackRender={() => <EditorErrorFallback />}>
        <StandaloneEditor {...props} ref={ref} />
      </ErrorBoundary>
    </AppProvider>
  );
});
